/* eslint formatjs/no-literal-string-in-jsx: "error" */
import React from "react"
import { useMapViewContext } from "../../context/MapViewContext"
import { useHistory } from "react-router-dom"
import { getProviderFinderSearchURL, ProviderFinderParam } from "../../utils/providerFinderSearchUrl"
import { useUserContext } from "../../context/UserContext"
import { colors, Container, FlexContainer, H4, H5, Icon, Text, useBreakpointComponent } from "@carrotfertility/carotene"
import styled from "styled-components"
import { FormattedMessage, useIntl } from "react-intl"

const LargeStyledButton = styled.button`
  background: white;
  border: 2px solid transparent;
  border-radius: 8px;
  display: block;
  width: 100%;
  height: 80px;
  cursor: pointer;
  padding: 0;
  overflow: hidden;
  background-origin: border-box;
  &:focus-visible {
    border: ${`2px solid ${colors.base.carrotOrange}`};
    outline: none;
  }
  &:hover {
    border: ${`2px solid ${colors.base.carrotOrange}`};
  }
`

const MediumStyledButton = styled(LargeStyledButton)`
  border-radius: 6px;
  height: 52px;
  padding: 14px 12px;
`

type SwitchViewButtonProps = {
  showMapView: boolean
  switchViewHandler: () => void
}

function SwitchViewButtonWithImage({ showMapView, switchViewHandler }: SwitchViewButtonProps): JSX.Element {
  const intl = useIntl()
  return (
    <LargeStyledButton
      onClick={switchViewHandler}
      id={showMapView ? "switch-to-list-view-button" : "switch-to-map-view-button"}
      style={{
        backgroundImage: showMapView
          ? `url(${require(`../../resources/images/list-view-background.png`)})`
          : `url(${require(`../../resources/images/map-view-background.png`)})`
      }}
    >
      <FlexContainer
        width="220px"
        height="26px"
        borderRadius="26px"
        direction="row"
        justifyContent="center"
        alignItems="center"
        gap="12px"
        background="white"
        sx={{
          margin: "auto"
        }}
      >
        <Icon name={showMapView ? "listView" : "mapView"} size="16px" color="carrotOrange"></Icon>
        <Text color={"carrotOrange"} weight="bold" size="13px">
          {showMapView
            ? intl.formatMessage({ defaultMessage: "LIST VIEW" })
            : intl.formatMessage({ defaultMessage: "MAP VIEW" })}
        </Text>
      </FlexContainer>
    </LargeStyledButton>
  )
}

function TextOnlySwitchViewButton({ showMapView, switchViewHandler }: SwitchViewButtonProps): JSX.Element {
  const intl = useIntl()
  return (
    <Container padding={"12px"}>
      <H4 padding="20px 12px 8px 12px">
        <FormattedMessage defaultMessage="View" />
      </H4>
      <MediumStyledButton
        onClick={switchViewHandler}
        id={showMapView ? "switch-to-list-view-button" : "switch-to-map-view-button"}
      >
        <FlexContainer direction="row" alignItems="center" gap="5px" background="white" sx={{ height: "100%" }}>
          <Icon name={showMapView ? "listView" : "mapView"} size="16px" color="carrotOrange"></Icon>
          <FlexContainer direction="row" alignItems="center" gap="5.5px">
            <H5 sx={{ fontWeight: "500", lineHeight: "21px", color: colors.base.carrotOrange }}>
              {showMapView
                ? intl.formatMessage({ defaultMessage: "Display list view" })
                : intl.formatMessage({ defaultMessage: "Display map view" })}
            </H5>
            <Icon name={"rightCarrotSmall"} color={"carrotOrange"} size={"6px"} />
          </FlexContainer>
        </FlexContainer>
      </MediumStyledButton>
    </Container>
  )
}

export function SwitchViewButton(): JSX.Element {
  const { showMapView, setShowMapView } = useMapViewContext()
  const history = useHistory()
  const { companyCountryCode } = useUserContext()

  const updateSearchURL = (): void => {
    const params = new URLSearchParams(history.location.search)
    const newSearchURL = getProviderFinderSearchURL({
      searchLocation: params.get(ProviderFinderParam.Search),
      radiusInKm: "", // set to anywhere in the country
      companyCountryCode,
      locationDetails: {
        latitude: Number(params.get(ProviderFinderParam.Latitude)),
        longitude: Number(params.get(ProviderFinderParam.Longitude)),
        countryCode: params.get(ProviderFinderParam.Country),
        isBroadArea: false
      },
      carrotPartner: params.get(ProviderFinderParam.CarrotPartner) === "true",
      providerType: params.get(ProviderFinderParam.ProviderType)
    })

    history.push({
      pathname: newSearchURL.pathname,
      search: newSearchURL.search
    })
  }

  const switchViewHandler = (): void => {
    if (!showMapView) {
      updateSearchURL()
    }
    setShowMapView(!showMapView)
  }

  const switchViewButton = useBreakpointComponent([
    <TextOnlySwitchViewButton showMapView={showMapView} switchViewHandler={switchViewHandler} key={"small"} />,
    <TextOnlySwitchViewButton showMapView={showMapView} switchViewHandler={switchViewHandler} key={"medium"} />,
    <TextOnlySwitchViewButton showMapView={showMapView} switchViewHandler={switchViewHandler} key={"large"} />,
    <SwitchViewButtonWithImage showMapView={showMapView} switchViewHandler={switchViewHandler} key={"x-large"} />
  ])
  return switchViewButton || <></>
}
